import moment from 'moment';
import { IEnhancedPdfDetails, IPdfDetails, IPourExportDetailsItem } from '@/models';
import { printDivId } from '@/constants';
import { useEffect, useMemo } from 'react';
import { useCubeOrCylinder, useDateTimeFormat, useUnitType } from '@/hooks';
import { cubeToCelinderandPsiText } from '@/utils';

interface PourPdfPageProps {
  jsonData: IPourExportDetailsItem[];
  pdfDetails: IEnhancedPdfDetails
  generatePDFFromHTML: (pourExportDetails: IPourExportDetailsItem[], pdfDetails: IPdfDetails)=>void
  isRequestedToPrint: boolean
}
const PourPdfPage = ({
  jsonData,
  pdfDetails,
  generatePDFFromHTML,
  isRequestedToPrint,
}: PourPdfPageProps) => {
  const { isMetric } = useUnitType();
  const { isCylinder } = useCubeOrCylinder();
  const unit = isMetric? 'MPa': 'PSI';
  const styles = {
    wholePage: {
      display: 'none',
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: '20px 40px',
    },
    topTableTd: {
      border: 0,
    },
    section: {
      marginBottom: '20px',
    },
    h1: {
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'normal',
      fontSize: '20px',
    },
    detailsTableTd: {
      padding: '10px',
      textAlign: 'left',
    },
    detailsTableTh: {
      backgroundColor: '#f2f2f2',
      textAlign: 'left',
      padding: '10px',
    },
    columnLayout: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '3rem 0 5rem 0',
      gap: '2rem',
    },

    column: {
      display: 'flex',
      flexDirection: 'column',
    },

    spacer2: {
      height: '2rem',
    },

    fullWidth: {
      width: '100%',
    },

    marginb0: {
      marginBottom: 0,
    },
  } as const;
  const { formatDate } = useDateTimeFormat();

  const totalAverageStrength = useMemo(() => {
    if (jsonData && Array.isArray(jsonData) && jsonData.length > 0) {
      const validNumbers = jsonData
        .filter(pourDetails=>!pourDetails.is_disabled)
        .map(item => item.strength_derivative_1)
        .filter((val): val is number => val !== null && !isNaN(val));

      const average =
        validNumbers.length > 0
          ? validNumbers.reduce((sum, num) => sum + num, 0) / validNumbers.length
          : 0; 

      return cubeToCelinderandPsiText(average,isMetric,isCylinder);
    } else {
      return 'N/A';
    }
  }, [jsonData]);

  useEffect(() => {
    if (isRequestedToPrint) {
      generatePDFFromHTML(jsonData,pdfDetails);
      isRequestedToPrint = false
    }
  }, [isRequestedToPrint]);

  
  return (
    <div id={printDivId} style={styles.wholePage}>
      <div style={styles.section}>
        <img
          style={{ width: '25%', height: '25%' }}
          src={process.env.PUBLIC_URL + '/full_logo_new.png'}
          alt='Greenvibe'
        />
      </div>
      <div style={styles.section}>
        <h1 style={styles.h1}>
          Strength Report:{'   '}
          {formatDate(new Date(), 'time')}
        </h1>
      </div>

      <div style={styles.section}>
        <table className='top-details-table' style={{ border: 'none'}}>
          <tbody>
            <tr>
              <td style={styles.topTableTd}>Project name:</td>
              <td style={styles.topTableTd}>{pdfDetails.projectName}</td>
            </tr>
            <tr>
              <td style={styles.topTableTd}>Export by:</td>
              <td style={styles.topTableTd}>{pdfDetails.userName}</td>
            </tr>
            <tr>
              <td style={styles.topTableTd}>Element tested:</td>
              <td style={styles.topTableTd}>{pdfDetails.pourDataName}</td>
            </tr>
            <tr>
              <td style={styles.topTableTd}>Concrete type:</td>
              <td style={styles.topTableTd}>{pdfDetails.concreteType}</td>
            </tr>
            <tr>
              <td style={styles.topTableTd}>Cast start time:</td>
              <td style={styles.topTableTd}>
                {formatDate(pdfDetails.pourStartTime, 'time')}
              </td>
            </tr>
            <tr>
              <td style={styles.topTableTd}>Days from casting:</td>
              <td style={styles.topTableTd}>
                {moment(pdfDetails.pourStartTime).isValid()
                  ? moment().diff(moment(pdfDetails.pourStartTime), 'day')
                  : ''}
              </td>
            </tr>
          </tbody>
        </table>

        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            marginTop: '20px',
          }}
        >
          <thead>
            <tr>
              <th style={styles.detailsTableTh}>Device name</th>
              <th style={styles.detailsTableTh}>Device location</th>
              <th style={styles.detailsTableTh}>Strength [{unit}]</th>
              <th style={styles.detailsTableTh}>Last Measurement</th>
              <th style={styles.detailsTableTh}>Pour start time</th>
            </tr>
          </thead>
          <tbody>
            {jsonData.map(item => (
              <tr>
                <td style={styles.detailsTableTd}>
                  {item.display_name?.replace('GV_Device_', '')} 
                  {item.is_disabled && <span style={{color:"red"}}> (Disabled)</span>}
                </td>
                <td style={styles.detailsTableTd}>{item.location}</td>
                <td style={styles.detailsTableTd}>
                  {cubeToCelinderandPsiText((parseFloat(item.strength_derivative_1 as any)),isMetric,isCylinder)}
                </td>
                <td style={styles.detailsTableTd}>
                  {formatDate(item.sensor_ts, 'time')}
                </td>
                <td style={styles.detailsTableTd}>
                  {formatDate(item.cast_start_time_utc, 'time')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ float: 'left',marginLeft: '200px', marginTop: '10px' }}>
          <b>Average strength: {totalAverageStrength} [{unit}] </b>
        </div>
      </div>
    </div>
  );
};

export default PourPdfPage;
